/** Data Quality queue — owner/admin review of abnormalities that migration
 * (and, going forward, any import/reconciliation job) could not safely
 * auto-correct on its own (Prompt 11F.1). Resolving here does NOT touch the
 * underlying record — an admin fixes the data through its normal edit flow
 * first, then marks the flag resolved (or dismisses it if no action is
 * needed), keeping this a pure audit trail like the Requests queue.
 */

import React, { useState } from 'react';
import {
  useDataQualityQueue,
  useDismissDataQualityFlag,
  useResolveDataQualityFlag,
  type DataQualityFlag,
} from '../api/dataQuality';

const card: React.CSSProperties = {
  background: '#fff',
  border: '1px solid #e5e7eb',
  borderRadius: '0.5rem',
  padding: '1rem 1.25rem',
  marginBottom: '0.75rem',
};

const SEVERITY_COLOR: Record<string, string> = {
  info: '#2563eb',
  warning: '#d97706',
  critical: '#dc2626',
};

const FlagRow: React.FC<{ flag: DataQualityFlag }> = ({ flag }) => {
  const [note, setNote] = useState('');
  const resolve = useResolveDataQualityFlag();
  const dismiss = useDismissDataQualityFlag();
  const isOpen = flag.status === 'open';
  const busy = resolve.isPending || dismiss.isPending;

  return (
    <div style={card} data-testid={`candeza-data-quality-row-${flag.id}`}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <span
            style={{
              display: 'inline-block',
              fontSize: '0.6875rem',
              fontWeight: 700,
              textTransform: 'uppercase',
              color: SEVERITY_COLOR[flag.severity] ?? '#6b7280',
              marginBottom: 4,
            }}
          >
            {flag.severity} · {flag.category}
          </span>
          <div style={{ fontWeight: 600 }}>{flag.title}</div>
          <div style={{ fontSize: '0.8125rem', color: '#6b7280', marginTop: 2 }}>
            {flag.entity_type}: {flag.entity_label}
          </div>
          <div style={{ fontSize: '0.8125rem', color: '#374151', marginTop: 4 }}>{flag.detail}</div>
          {flag.suggested_action && (
            <div style={{ fontSize: '0.8125rem', color: '#2563eb', marginTop: 4 }}>
              Suggested: {flag.suggested_action}
            </div>
          )}
          <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: 4 }}>
            Source: {flag.source} · Assigned: {flag.target_type === 'role' ? flag.target_value : 'you'}
          </div>
        </div>
        <span style={{ fontWeight: 600, textTransform: 'capitalize' }}>{flag.status}</span>
      </div>

      {isOpen && (
        <div style={{ marginTop: '0.75rem', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <input
            placeholder="Note (optional)"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            data-testid={`candeza-data-quality-note-${flag.id}`}
            style={{ flex: 1 }}
          />
          <button
            disabled={busy}
            onClick={() => resolve.mutate({ id: flag.id, body: { note: note || undefined } })}
            data-testid={`candeza-data-quality-resolve-${flag.id}`}
          >
            Mark Resolved
          </button>
          <button
            disabled={busy}
            onClick={() => dismiss.mutate({ id: flag.id, body: { note: note || undefined } })}
            data-testid={`candeza-data-quality-dismiss-${flag.id}`}
          >
            Dismiss
          </button>
        </div>
      )}
    </div>
  );
};

const DataQualityPage: React.FC = () => {
  const [statusFilter, setStatusFilter] = useState<'open' | 'resolved' | 'dismissed' | ''>('open');
  const { data, isLoading, isError } = useDataQualityQueue(statusFilter || undefined);
  const flags = data?.items ?? [];

  return (
    <div data-testid="candeza-data-quality-page">
      <h1 style={{ fontSize: '1.25rem', fontWeight: 700, marginBottom: '0.25rem' }}>Data Quality</h1>
      <p style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '1rem' }}>
        Abnormalities flagged by migration and reconciliation jobs — review before treating the
        underlying data as trustworthy.
      </p>

      <div style={{ marginBottom: '1rem' }}>
        {(['open', 'resolved', 'dismissed', ''] as const).map((s) => (
          <button
            key={s || 'all'}
            onClick={() => setStatusFilter(s)}
            data-testid={`candeza-data-quality-filter-${s || 'all'}`}
            style={{ marginRight: '0.5rem', fontWeight: statusFilter === s ? 700 : 400 }}
          >
            {s || 'All'}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div data-testid="candeza-data-quality-loading">Loading…</div>
      ) : isError ? (
        <div data-testid="candeza-data-quality-error" style={{ color: '#ef4444' }}>
          Could not load the data-quality queue.
        </div>
      ) : flags.length === 0 ? (
        <p data-testid="candeza-data-quality-empty" style={{ color: '#6b7280' }}>
          Nothing here.
        </p>
      ) : (
        <div data-testid="candeza-data-quality-list">
          {flags.map((f) => (
            <FlagRow key={f.id} flag={f} />
          ))}
        </div>
      )}
    </div>
  );
};

export default DataQualityPage;
